import "Components/ConfigLayout/System/ScheduleSettings/ScheduleSettings.less";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { type ChangeEvent, type FormEvent, memo, useState } from "react";
import {
	Button, ButtonTypes, ConfirmModal, type Column, Icon, IconSize, type ModalButtonProps, Input, type SelectOption,
	type WithKey, Select, Table, SettingsLayout, Card
} from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectSchedule, setScheduleAction } from "Data/Redux/Slices/Settings/System/Schedule";
import { produce } from "immer";
import { isEqual } from "lodash";
import type { ScheduledDay, ScheduleDays } from "Interfaces";
import { ScheduleType } from "Data/Objects/System";
import SchedulePopover from "Components/ConfigLayout/System/ScheduleSettings/SchedulePopover";

const typeOptions: SelectOption[] = [
	{
		label: "Daily",
		value: ScheduleType.DAILY
	},
	{
		label: "Weekly",
		value: ScheduleType.WEEKLY
	}
];

const days = [ "MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN" ];

const createTimeOptions = (day?: string) => {
	return new Array(24).fill(0).map((current, index) => {
		return {
			label: `${ day ? day + ". " : "" }${ index.toString().padStart(2, "0") }:00`,
			value: `${ day ? day + ". " : "" }${ index.toString().padStart(2, "0") }:00`
		};
	});
};

const dailyTimeOptions: SelectOption[] = createTimeOptions();
const weeklyTimeOptions: SelectOption[] = days.flatMap(createTimeOptions);
const columns: Column[] = [
	{
		align: "center",
		dataIndex: "no",
		key: "no",
		sortable: false,
		title: "No."
	},
	{
		dataIndex: "name",
		key: "name",
		sortable: false,
		title: "Name",
		width: 190
	},
	{
		dataIndex: "type",
		key: "type",
		sortable: false,
		title: "Type",
		width: 190
	},
	{
		dataIndex: "startTime",
		key: "startTime",
		sortable: false,
		title: "Start Time",
		width: 190
	},
	{
		dataIndex: "endTime",
		key: "endTime",
		sortable: false,
		title: "End Time",
		width: 190
	},
	{
		dataIndex: "delete",
		key: "delete",
		sortable: false,
		title: "Delete"
	}
];

function ScheduleSettings() {
	const dispatch = useAppDispatch();
	const scheduleSettings = useAppSelector(selectSchedule);
	const [ localScheduleSettings, setLocalScheduleSettings ] = useState(scheduleSettings);
	const setScheduleSettings = () => dispatch(setScheduleAction(localScheduleSettings));
	const disabled = isEqual(localScheduleSettings, scheduleSettings);

	const handleFormReset = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		setLocalScheduleSettings(scheduleSettings);
	};

	const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setScheduleSettings();
		}
	};

	const handleNameUpdate = (id: number) => (value: string) => {
		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].name = value;
		}));
	};

	const handleScheduleTypeSelect = (id: number) => (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].scheduleType = value as ScheduleType;

			switch (value) {
				case ScheduleType.DAILY: {
					draft.schedules[ id ].startTime = "00:00";
					draft.schedules[ id ].endTime = "00:00";

					break;
				}

				case ScheduleType.WEEKLY: {
					draft.schedules[ id ].startTime = "MON. 00:00";
					draft.schedules[ id ].endTime = "MON. 00:00";

					break;
				}

				default: {
					break;
				}
			}
		}));
	};

	const checkScheduledDay = (id: number) => (value: ScheduledDay) => {
		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].scheduledDay = value;
		}));
	};

	const changeCheckbox = (day: string, id: number) => (event: ChangeEvent<HTMLInputElement>) => {
		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].scheduledDay[ day as ScheduleDays ] = event.target.checked;
		}));
	};

	const selectStartTime = (id: number) => (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].startTime = value;
		}));
	};

	const selectEndTime = (id: number) => (value: string | null) => {
		if (!value) {
			return;
		}

		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules[ id ].endTime = value;
		}));
	};

	const deleteRow = (id: number) => () => {
		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules = localScheduleSettings.schedules.filter((current, index) => index !== id);
		}));
	};

	const tableData: WithKey[] = localScheduleSettings.schedules.map((schedule, index) => {
		const { name, scheduleType, scheduledDay, startTime, endTime } = schedule;
		const nameField = (
			<Input
				onUpdate={ handleNameUpdate(index) }
				value={ name }
			/>
		);

		const scheduleTypeField = (
			<div className="schedule-type">
				<Select
					allowClear={ false }
					onSelect={ handleScheduleTypeSelect(index) }
					options={ typeOptions }
					value={ scheduleType }
				/>
				<SchedulePopover
					checkScheduledDay={ checkScheduledDay }
					days={ days }
					index={ index }
					scheduledDay={ scheduledDay }
					scheduleType={ scheduleType }
					changeCheckbox={ changeCheckbox }
				/>
			</div>
		);

		const timeField = (key: string, time: string) => {
			const handleTimeSelect = key === "startTime" ? selectStartTime(index) : selectEndTime(index);

			return (
				<Select
					allowClear={ false }
					onSelect={ handleTimeSelect }
					options={ scheduleType === ScheduleType.WEEKLY ? weeklyTimeOptions : dailyTimeOptions }
					value={ time }
				/>
			);
		};

		const cancelButton: ModalButtonProps = {
			text: "Cancel",
			type: ButtonTypes.DANGER
		};

		const confirmButton: ModalButtonProps = {
			text: "Confirm",
			type: ButtonTypes.TERTIARY,
			onClick: deleteRow(index)
		};

		const deleteField = (
			<ConfirmModal
				cancelButton={ cancelButton }
				okButton={ confirmButton }
				title="Delete Preset"
				modalContent="Are you sure you want to remove this preset?"
			>
				<div className="table-icon-button" >
					<Icon name="trash" size={ IconSize.SMALLER } />
				</div>
			</ConfirmModal>
		);

		return {
			no: index + 1,
			name: nameField,
			type: scheduleTypeField,
			startTime: timeField("startTime", startTime),
			endTime: timeField("endTime", endTime),
			delete: deleteField,
			key: index + ""
		};
	});

	const handleCreateNewScheduleClick = () => {
		const newSchedule = {
			name: "NEW SCHEDULE",
			scheduleType: ScheduleType.DAILY,
			scheduledDay: {
				MON: true,
				TUE: true,
				WED: true,
				THU: true,
				FRI: true,
				SAT: true,
				SUN: true
			},
			startTime: "00:00",
			endTime: "00:00"
		};

		setLocalScheduleSettings(produce(localScheduleSettings, draft => {
			draft.schedules.push(newSchedule);
		}));
	};

	return (
		<SettingsLayout.Form
			className="schedule-settings-form schedule-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="large" title="Schedule Preset">
					<div className="form-row">
						<Button
							htmlType="button"
							icon={ { name: "plus" } }
							onClick={ handleCreateNewScheduleClick }
							type={ ButtonTypes.TERTIARY }
						>
							Add Preset
						</Button>
					</div>
					<Table
						columns={ columns }
						data={ tableData }
					/>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(ScheduleSettings);
